// PenTabs — CSP でいう「タブ (ツールグループ)」を表す純粋 domain モデル。
//
// 由来 (docs/_wip/shortcut-settings.md「CSP の実モデル: キーは『ツール』に付き、同キー
// 同士が循環する」2026-08-21 確定):
//   - CSP はショートカットを「個別ペン」「タブ」の各粒度に割当てられる。同じキーを
//     割り当てられた対象は押すたびに循環し、タブへ割り当てた場合の意味は「そのタブで
//     最後に使ったペンを選ぶ」。
//   - **Classic / フォンにもタブの UI は無いが、概念として生かす** (同 md「レイアウト差」):
//     ペンは内部的に必ずこの 3 タブのどれかに属するので、循環の順序と挙動は
//     Classic / Modern / フォンで完全に同一になる (= UI に見えないものが挙動に影響する
//     ことは許容する、というのが作者の優先判断)。
//   - タブの内容 (線画=ブラシ/サインペン/ドット/トーン、塗り=エアブラシ、
//     質感=テクスチャ/クレヨン/パステル) は Modern 常設ブラシパネル
//     (`ModernBrushPanel.tsx` §14.2 の表) と **同じ分類**を使う。ここが「どのタブに
//     どの StrokePen が属するか」の single source of truth (= UI 表示にもショートカット
//     循環にも共有される、旧実装は `ModernBrushPanel.tsx` ローカルの `PEN_TO_TAB` に
//     閉じていたが、Classic からも参照できるようここへ移した)。
//   - 消しゴムはこの 3 タブのどれにも属さない (`PEN_TO_TAB` に現れない)。ただし
//     ショートカット循環そのものには「タブに属さないフラットな対象」として参加できる
//     (2026-08-21 訂正、同 md「消しゴムも循環に入る」— 割当先の種類はタブ経由の解決規則
//     の有無を除き循環の可否に影響しない)。

import type { StrokePen } from '@oekaki/protocol';

export type PenTabId = 'line' | 'fill' | 'texture';

export interface PenTabDef {
  readonly id: PenTabId;
  readonly label: string;
  /** このタブに属する pen (表示順 = タブ内循環順、shortcut-settings.md「循環順序は
   * システム決め打ち = 設定画面の並び順」の基礎になる)。 */
  readonly pens: readonly StrokePen[];
}

/**
 * タブの唯一の定義元 (`ModernBrushPanel.tsx` §14.2 の表と同じ分類)。**新しいペンを
 * 追加するときはまずこの配列のどのタブに入れるかを決める** (= UI とショートカット循環の
 * 両方に効く、器 (タブの本数) を作り直す必要は無い)。
 */
export const PEN_TABS: readonly PenTabDef[] = [
  { id: 'line', label: '線画', pens: ['brush', 'flat', 'dot', 'tone'] },
  { id: 'fill', label: '塗り', pens: ['airbrush'] },
  { id: 'texture', label: '質感', pens: ['texture', 'crayon', 'pastel'] },
];

/** 全タブ ID (安定列)。 */
export const ALL_PEN_TAB_IDS: readonly PenTabId[] = PEN_TABS.map((t) => t.id);

/** `pen` からそれを含むタブを引く逆引き表 (= 自動追従・ショートカット循環の両方で使う)。 */
export const PEN_TO_TAB: Partial<Record<StrokePen, PenTabId>> = (() => {
  const map: Partial<Record<StrokePen, PenTabId>> = {};
  for (const tab of PEN_TABS) {
    for (const pen of tab.pens) {
      map[pen] = tab.id;
    }
  }
  return map;
})();

/** 現在の pen を含むタブ ID (無ければ null、= 消しゴム等タブに属さない pen)。 */
export const tabForPen = (pen: StrokePen): PenTabId | null => PEN_TO_TAB[pen] ?? null;

/** タブの定義を id で引く (見つからなければ定義漏れ、呼び出し側のバグとして throw)。 */
export const penTabById = (id: PenTabId): PenTabDef => {
  const tab = PEN_TABS.find((t) => t.id === id);
  if (!tab) throw new Error(`PenTabs.ts に PenTabId='${id}' の定義がありません`);
  return tab;
};

/**
 * そのタブを一度も使っていないセッションでの既定「最後に使ったペン」(= タブの
 * 先頭ペン)。`useRoomChrome` の `lastUsedPenForTab` 初期値に使う (= タブへショート
 * カット割当てた直後に押しても、タブの代表ペンへ落ちる)。
 */
export const DEFAULT_PEN_FOR_TAB: Readonly<Record<PenTabId, StrokePen>> = (() => {
  const map = {} as Record<PenTabId, StrokePen>;
  for (const tab of PEN_TABS) {
    const first = tab.pens[0];
    if (first === undefined) {
      throw new Error(`PenTabs.ts: タブ '${tab.id}' に pen がありません`);
    }
    map[tab.id] = first;
  }
  return map;
})();
